// BaseWriteService.js
//
// Service for writing base data via Cloud Functions (Ai: Base).

import { functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

/**
 * Creates a new base in Firestore.
 *
 * @param {string} userKey - The user key associated with the base.
 * @param {string} title - The title of the base.
 * @param {Object|null} [ownerProfile=null] - Optional owner identity details.
 * @param {object} [options]
 * @param {string} [options.visibility='private'] - Base visibility.
 *
 * @returns {Promise<Object|null>} The created base data or null if userKey is not provided.
 */
export async function createBase(
  userKey,
  title,
  ownerProfile = null,
  { visibility = 'private' } = {},
) {
  if (!userKey) return null;

  const { data } = await callable('createBase')({
    title,
    ownerProfile,
    visibility,
  });

  return data || null;
}

/**
 * Updates an existing base in Firestore.
 *
 * @param {string} baseKey - The key of the base to update (wire: baseKey).
 * @param {object} data - The data to update the base with.
 *
 * @returns {Promise<void>}
 */
export async function updateBase(baseKey, data) {
  if (!baseKey || !data) return;

  const payload = {
    ...data,
  };

  if (Object.prototype.hasOwnProperty.call(payload, 'ownerKey')) {
    delete payload.ownerKey;
  }

  await callable('updateBase')({
    ...buildTenantCallableFields(baseKey),
    baseKey: baseKey,
    data: payload,
  });
}

/**
 * Assigns a free self-serve plan to a base via Cloud Function.
 *
 * @param {object} input - Update input.
 * @param {string} input.baseKey - Base key (wire: baseKey).
 * @param {string} input.planKey - Selected plan key.
 *
 * @returns {Promise<object>} Assignment result.
 */
export async function updateBasePlanSelection({
  baseKey,
  planKey,
}) {
  const cleanedBaseKey = String(baseKey || '').trim();
  const cleanedPlanKey = String(planKey || '').trim();

  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const fn = callable('assignBaseSelfServePlan');
  const { data } = await fn({
    ...buildTenantCallableFields(cleanedBaseKey),
    planKey: cleanedPlanKey,
  });

  return data || null;
}

/**
 * Deletes a base by its key.
 *
 * @param {string} baseKey - The key of the base to delete (wire: baseKey).
 *
 * @returns {Promise<Object|null>} Operation metadata for the queued base delete.
 */
export async function deleteBaseByKey(baseKey) {
  if (!baseKey) return;

  const { data } = await callable('deleteBase')({
    ...buildTenantCallableFields(baseKey),
  });

  return data || null;
}
